import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, Clock, MessageCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../../data/mockData';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { contactAPI } from '../../services/api';
import { sendContactMessage } from '../../services/emailService';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submissionData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      subject: 'Homepage Contact Form',
      message: formData.message,
    };

    // Use centralized API
    const response = await contactAPI.submit(submissionData);

    if (response.ok) {
      console.log('✅ Message saved to Firestore');

      // Send email notification
      try {
        await sendContactMessage(submissionData);
        console.log('✅ Email notification sent');
      } catch (emailError) {
        console.warn('⚠️ Email sending failed:', emailError);
      }

      // Dispatch event for real-time notification (admin UI compat)
      window.dispatchEvent(new CustomEvent('newMessage', {
        detail: { ...submissionData, created_at: new Date().toISOString(), read: false }
      }));

      setLoading(false);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 4000);
    } else {
      console.error('❌ Submission failed:', response.detail);
      setLoading(false);
      // Optional: error toast
    }
  };


  const contactCards = [
    {
      icon: MapPin,
      title: 'Visit Us',
      content: '11 Mile Post, Monaragala Road, Buttala',
      subContent: 'Sri Lanka',
      color: 'from-teal-500 to-emerald-500',
      action: null
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: '+94 55 313 0227',
      subContent: '+94 70 601 0100',
      color: 'from-blue-500 to-cyan-500',
      action: 'tel:+94553130227'
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: 'pridepathlk@gmail.com',
      subContent: 'Quick response guaranteed',
      color: 'from-purple-500 to-pink-500',
      action: 'mailto:pridepathlk@gmail.com'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      content: 'Mon - Fri : 8:30 AM - 5:00 PM',
      subContent: 'Sat : 8:30 AM - 1:00 PM',
      extraContent: 'Sunday and other Mercantile Holidays : Closed',
      color: 'from-orange-500 to-amber-500',
      action: null
    }
  ];

  return (
    <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#2A9D8F]/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#2A9D8F]/5 rounded-full translate-x-1/3 translate-y-1/3" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-full text-sm font-semibold mb-4">
            Contact Us
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4">
            Get in Touch with Us
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
            Have questions about our financial services? We're here to help you achieve your goals.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10 md:mb-16">
          {contactCards.map((card, index) => {
            const Icon = card.icon;
            const CardWrapper = card.action ? 'a' : 'div';
            const cardProps = card.action ? { href: card.action } : {};

            return (
              <CardWrapper
                key={index}
                {...cardProps}
                className={`group bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${card.action ? 'cursor-pointer' : ''}`}
              >
                {/* Icon */}
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={22} className="text-white sm:hidden" />
                  <Icon size={24} className="text-white hidden sm:block" />
                </div>

                {/* Content */}
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{card.title}</h3>
                <p className="text-gray-800 font-medium text-sm sm:text-base">{card.content}</p>
                <p className="text-gray-500 text-xs sm:text-sm mt-1">{card.subContent}</p>
                {card.extraContent && (
                  <p className="text-gray-500 text-xs mt-1">{card.extraContent}</p>
                )}
              </CardWrapper>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Contact Form - Takes 3 columns */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6 sm:mb-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2A9D8F] to-emerald-500 flex items-center justify-center">
                  <MessageCircle size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Send us a Message</h3>
                  <p className="text-gray-500 text-sm">We'll get back to you within 24 hours</p>
                </div>
              </div>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6 animate-bounce">
                    <CheckCircle size={40} className="text-green-500" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h4>
                  <p className="text-gray-500 text-center max-w-sm">
                    We've received your message and will get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent focus:bg-white outline-none transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent focus:bg-white outline-none transition-all"
                        placeholder="+94 XX XXX XXXX"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent focus:bg-white outline-none transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent focus:bg-white outline-none transition-all resize-none"
                      placeholder="How can we help you today?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#2A9D8F] to-emerald-500 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#2A9D8F]/30 transition-all duration-300 disabled:opacity-70"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Map & Quick Actions - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Map */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="h-64 bg-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3962.456!2d81.2066!3d6.7415!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwNDQnMjkuNCJOIDgxwrAxMicyMy44IkU!5e0!3m2!1sen!2slk!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pride Path Commercial Location"
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Our Location</p>
                  <p className="text-gray-500 text-sm">Buttala, Sri Lanka</p>
                </div>
                <a
                  href="https://maps.google.com/?q=11+Mile+Post,+Monaragala+Road,+Buttala"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-lg text-sm font-semibold hover:bg-[#2A9D8F] hover:text-white transition-colors"
                >
                  Get Directions
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-gradient-to-br from-[#2A9D8F] to-emerald-600 rounded-2xl p-6 text-white">
              <h3 className="text-xl font-bold mb-4">Need Immediate Help?</h3>
              <p className="text-white/80 text-sm mb-6">
                Our customer service team is available during business hours to assist you.
              </p>
              <div className="space-y-3">
                <a
                  href="tel:+94553130227"
                  className="flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Phone size={18} />
                    <span className="font-medium">Call Us Now</span>
                  </div>
                  <ArrowRight size={18} />
                </a>
                <Link
                  to="/contact"
                  className="flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Mail size={18} />
                    <span className="font-medium">Visit Contact Page</span>
                  </div>
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
